import {
    apply,
    assoc,
    chain,
    compose,
    converge,
    has,
    identity,
    ifElse,
    lensProp,
    map,
    mergeAll,
    partial,
    pluck,
    prop,
    props,
    view,
    zip,
    zipObj,
    type KeyValuePair,
} from "ramda";

import type {
    FieldMeta,
    FormatFn,
    Fund,
    FundData,
    FundExplorerDataIndexed,
    Listing,
    ListingData,
    MetaData,
    MetaDataFieldProp,
    NestedChildProp,
    RawValue,
    ShareClass,
    ShareClassData,
    TopLevelDataObj,
    TopLevelDataType,
    TopLevelObj,
    TopLevelObjWithParentId,
} from "./types";

type TopLevelDataObjSansLocalId = Omit<TopLevelDataObj, "localId">;

export default function transformer(
    formatter: FormatFn<RawValue>,
    metaData: MetaData,
): (list: Fund[]) => FundExplorerDataIndexed {
    const getRawData = view<Fund | ShareClass | Listing, RawValue[]>(
        lensProp("data"),
    );
    const getMetaData = partial(
        (meta, p: MetaDataFieldProp) =>
            view<MetaData, FieldMeta[]>(lensProp(p), meta),
        [metaData],
    );
    const getCodeNames = compose(pluck("code_name"), getMetaData) as (
        ...args: unknown[]
    ) => any[];

    const createFieldMetaRawValuePair = (
        p: MetaDataFieldProp,
        obj: TopLevelObj,
    ) => zip(getMetaData(p), getRawData(obj));
    const createFormatterArgs = (p: MetaDataFieldProp, obj: TopLevelObj) =>
        map(
            (
                a: KeyValuePair<FieldMeta, RawValue>,
            ): [FieldMeta, RawValue, TopLevelObj] => [...a, obj],
            createFieldMetaRawValuePair(p, obj),
        );
    const getFormattedValues = (p: MetaDataFieldProp, obj: TopLevelObj) =>
        map(apply(formatter))(createFormatterArgs(p, obj));
    const createFormattedObj = (p: MetaDataFieldProp, obj: TopLevelObj) =>
        zipObj(getCodeNames(p), getFormattedValues(p, obj));
    const addType = (t: TopLevelDataType) => (obj: TopLevelObj) => {
        return assoc("type", t, obj) as TopLevelObj & { type: string };
    };
    const replaceRawData = (p: MetaDataFieldProp) => (obj: TopLevelObj) =>
        assoc(
            "data",
            createFormattedObj(p, obj),
            obj,
        ) as TopLevelDataObjSansLocalId;
    const getChildrenIds = (p: NestedChildProp) =>
        compose(pluck("id"), prop(p)) as (obj: any) => number[];
    const replaceNestedChildrenWithIds = (p: NestedChildProp) =>
        converge(
            (a: number[], b: TopLevelObj) => assoc(p, a, b),
            [getChildrenIds(p), identity<TopLevelObj>],
        );
    const replaceTopLevelObjChildrenWithIds = (p: NestedChildProp) =>
        ifElse(
            has(p),
            replaceNestedChildrenWithIds(p) as (
                x: TopLevelDataObjSansLocalId,
            ) => FundData | ShareClassData,
            identity,
        ) as (
            x: TopLevelDataObjSansLocalId,
        ) => ShareClassData | FundData | ListingData;

    const getTypeFromFieldProp = (p: MetaDataFieldProp): TopLevelDataType => {
        const [type, ..._] = p.split("_");
        switch (type) {
            case "share":
                return "shareclass";
            default:
                return type as TopLevelDataType;
        }
    };

    const getFormattedDataList = (p: MetaDataFieldProp, np: NestedChildProp) =>
        map(
            compose(
                replaceTopLevelObjChildrenWithIds(np),
                replaceRawData(p),
                addType(getTypeFromFieldProp(p)),
            ),
        );
    const addParentId =
        (parentType: string) =>
        (id: number, objects: Array<ShareClass | Listing>) =>
            map(assoc("parentId", `${parentType}-${id}`), objects);

    const getNestedChildren =
        (p: "share_classes" | "listings") => (obj: TopLevelObj) =>
            ifElse(
                has(p),
                compose(
                    apply(
                        addParentId(p === "listings" ? "shareclass" : "fund"),
                    ),
                    props(["id", p]) as (
                        x: any,
                    ) => KeyValuePair<number, TopLevelObj[]>,
                ),
                () => [],
            )(obj) as TopLevelObjWithParentId[] | [];

    // const indexById = (objs: TopLevelDataObjSansLocalId[]) =>
    //     zipObj(pluck("id", objs), objs);
    const indexById = (objs: TopLevelDataObjSansLocalId[]) => {
        return objs
            .map((obj) => ({ ...obj, localId: `${obj.type}-${obj.id}` }))
            .reduce((acc, o) => ({ ...acc, [o.localId]: o }), {});
    };

    const getFundData = getFormattedDataList("fund_fields", "share_classes");

    const getShareClassData = chain(
        compose(
            getFormattedDataList("share_class_fields", "listings"),
            getNestedChildren("share_classes"),
        ),
    );
    const getListingData = chain(
        compose(
            getFormattedDataList("listing_fields", "none"),
            chain(getNestedChildren("listings")),
            getNestedChildren("share_classes"),
        ),
    );

    return (list: Fund[]) => {
        const funds = indexById(getFundData(list));
        const shareClasses = indexById(getShareClassData(list));
        const listings = indexById(getListingData(list));
        return mergeAll([funds, shareClasses, listings]);
    };
}
